// Quote Builder — the retailer's supplier discount SHEET (migration 0094).
//
// Which row of the sheet applies to a product. Mirrors, line for line,
// kitchenos-api/modules/discount_rate_match.js (which counts the "Matches"
// column on the Suppliers page). Change one, change both:
// tests/discount_rate_match.test.js there and verify-qb-discount-rates.ts
// here pin the same cases.
//
// Order:
//   1. Discount code — the catalogue row's own discount code (PWS publishes
//      these on its price list; 217 of the 234 codes on SJB's sheet are in
//      the catalogue). Never a product-code prefix: PWS "6.10" covers 787
//      drawer-system rows with unrelated SKUs. A product that HAS a code is
//      decided by its code alone — a code not on the sheet gets no sheet
//      rate, never a range's discount by name (PWS "ZZ" bespoke rows are
//      named after stock ranges but are not priced like them).
//   2. Name, exact — range + colour, range, catalogue category, or a caller
//      extra ("Jigsaw") equals the row's description or one of its
//      matchNames. TKC's only route: its catalogue carries no codes.
//   3. Name, whole-word prefix, LONGEST wins — "Cambridge" reaches
//      "Cambridge Fir Green Doors"; "Lucente Gloss" beats "Lucente".
// Rows arrive newest-first (valid_from DESC) and already filtered to those in
// force today, so a tie goes to the newer sheet.
//
// Drilling is never a rate: it is priced per hole and never discounted.

export interface SheetRate {
  discountCode: string | null;
  description: string;
  matchNames: string[];
  discountPercent: number;
}

export interface RateLookup {
  discountCode?: string | null;
  doorRange?: string | null;
  doorColour?: string | null;
  categoryName?: string | null;
  /** Extra names, least specific last (e.g. ["Jigsaw"] for a Jigsaw line). */
  names?: readonly string[];
}

export function normName(s: unknown): string {
  return String(s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

function rowNames(r: SheetRate): string[] {
  return [normName(r.description), ...(r.matchNames ?? []).map(normName)].filter(Boolean);
}

/** Candidate names for a product / quote line, most specific first. */
export function candidateNames(l: RateLookup): string[] {
  const out: string[] = [];
  const range = String(l.doorRange ?? "").trim();
  const colour = String(l.doorColour ?? "").trim();
  if (range && colour) out.push(`${range} ${colour}`);
  if (range) out.push(range);
  if (l.categoryName) out.push(String(l.categoryName));
  for (const e of l.names ?? []) if (e) out.push(String(e));
  return out;
}

export function matchSheetRate(
  rates: readonly SheetRate[],
  l: RateLookup,
): { rate: SheetRate; by: "code" | "name" | "prefix" } | null {
  if (!rates.length) return null;
  const code = normName(l.discountCode);
  if (code) {
    const hit = rates.find((r) => normName(r.discountCode) === code);
    return hit ? { rate: hit, by: "code" } : null;
  }
  const cands = candidateNames(l).map(normName).filter(Boolean);
  for (const c of cands) {
    const hit = rates.find((r) => rowNames(r).includes(c));
    if (hit) return { rate: hit, by: "name" };
  }
  for (const c of cands) {
    let best: SheetRate | null = null;
    let bestLen = 0;
    for (const r of rates) {
      for (const n of rowNames(r)) {
        if (n.length > bestLen && c.startsWith(n + " ")) { best = r; bestLen = n.length; }
      }
    }
    if (best) return { rate: best, by: "prefix" };
  }
  return null;
}

/** Line codes that are a drilling charge — see drilling-aggregate.ts
 *  isDrillingCharge. Kept as a code test here so the resolver can refuse a
 *  discount for one without needing the whole line. */
export function isDrillingCode(code: unknown): boolean {
  const c = String(code ?? "");
  return /-DRILLING$/i.test(c) || /CIDD(ABS|BID)$/i.test(c);
}
